import { z } from "zod";

export type ValidationT = (key: string) => string;

export function createPetWeightSchema(t: ValidationT) {
  return z.object({
    weight: z.number().positive(t("weightPositive")).max(9999, t("weightMax")),
    measured_at: z.date().optional(),
  });
}

export function createPetSchema(t: ValidationT, deceasedStatusId?: string) {
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, t("petNameRequired"))
      .max(100, t("petNameTooLong")),
    record_number: z
      .string()
      .trim()
      .max(50, t("recordNumberTooLong"))
      .optional()
      .or(z.literal("")),
    owner_id: z.string().uuid(t("selectOwner")),
    gender_id: z.string().uuid().optional().or(z.literal("")),
    sterilization_id: z.string().uuid().optional().or(z.literal("")),
    birth_date: z
      .string()
      .optional()
      .or(z.literal(""))
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return date <= new Date();
        },
        { message: t("birthDateNotFuture") }
      ),
    species_id: z.string().uuid(t("speciesRequired")),
    breed_id: z.string().uuid().optional().or(z.literal("")),
    size_category_id: z.string().uuid().optional().or(z.literal("")),
    coat_type_id: z.string().uuid().optional().or(z.literal("")),
    color_id: z.string().uuid().optional().or(z.literal("")),
    status_id: z.string().uuid(t("statusRequired")),
    death_date: z.string().optional().or(z.literal("")),
    death_cause: z.string().trim().optional().or(z.literal("")),
    microchip_number: z.string().trim().optional().or(z.literal("")),
    health_plan: z.string().trim().optional().or(z.literal("")),
    pedigree_number: z.string().trim().optional().or(z.literal("")),
    notes: z.string().trim().optional().or(z.literal("")),
    current_weight: z.number().positive().max(9999, t("weightMax")).optional(),
    conditions: z.array(z.string().uuid()).optional(),
    allergies: z.array(z.string().uuid()).optional(),
    temperaments: z.array(z.string().uuid()).optional(),
    photo: z.any().optional(),
    attachments: z.array(z.any()).optional(),
    daycare_hotel_notes: z.string().optional(),
    daycare_hotel: z.any().optional(),
    daycare_hotel_initial: z.any().optional(),
  }).superRefine((data, ctx) => {
    if (!data.death_date) {
      if (deceasedStatusId && data.status_id === deceasedStatusId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["death_date"],
          message: t("deathDateRequired"),
        });
      }
      return;
    }
    const date = new Date(data.death_date);
    if (Number.isNaN(date.getTime()) || date > new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["death_date"],
        message: t("deathDateNotFuture"),
      });
    }
  });
}

export function createPetSchemaMinimal(t: ValidationT) {
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, t("petNameRequired"))
      .max(100, t("petNameTooLong")),
    record_number: z
      .string()
      .trim()
      .max(50, t("recordNumberTooLong"))
      .optional()
      .or(z.literal("")),
    owner_id: z.string().uuid(t("selectOwner")),
  });
}

const defaultT: ValidationT = (k) => k;
export const petWeightSchema = createPetWeightSchema(defaultT);
export const petSchema = createPetSchema(defaultT);
export const petSchemaMinimal = createPetSchemaMinimal(defaultT);

export type PetInput = z.infer<typeof petSchema>;
export type PetWeightInput = z.infer<typeof petWeightSchema>;
export type PetMinimalInput = z.infer<typeof petSchemaMinimal>;
